import type { SupProfileKey } from "./sport-profiles";
import {
  assessSupOffshoreDrift,
  computeSupSafety,
  computeSupSafetyV2,
  SUP_ALONG_COAST_CURRENT_LABEL,
  SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
  supAlongCoastCurrentDetail,
  supUvAndStormSafetyV2,
  type SupOffshoreDriftAssessment,
  type SupOffshoreDriftThresholds,
  type SupSafetyV2Input,
  type SupUvAndStormInput,
} from "./sup-scoring";
import { SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS } from "./sup-offshore-drift";
import { classifySuppliedCurrent } from "./seaward-current";
import { classifyWindAgainstTide, windAgainstTideDetail } from "./wind-against-tide";
import { classifyModelFog } from "./model-fog";

export type SupSurfSafetyInput = {
  swellHeightM: number | null;
  currentVelocityMs: number | null;
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  windDirectionDeg: number | null;
  beachFacingDeg: number | null;
  visibilityM: number | null;
  weatherCode: number | null;
  pollutionState: "clear" | "amber" | "red" | "unavailable";
  closureState: "open" | "closed" | "unavailable";
};

export type SupDownwindSafetyInput = {
  rideableSeaM: number | null;
  currentVelocityMs: number | null;
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  visibilityM: number | null;
  weatherCode: number | null;
  pollutionState: "clear" | "amber" | "red" | "unavailable";
  closureState: "open" | "closed" | "unavailable";
  routeBearingDeg: number | null;
  launchKnown: boolean | null;
  landingKnown: boolean | null;
  recoveryKnown: boolean | null;
};

export type SupTouringSafetyInput = Parameters<typeof computeSupSafety>[0];
export type SupProfileSafetyInput =
  | SupTouringSafetyInput
  | SupSurfSafetyInput
  | SupDownwindSafetyInput;

export type SupProfileSafetyIndicator = {
  key: string;
  severity: "green" | "amber" | "red" | "unavailable";
  label: string;
  detail: string;
  confidence?: "full" | "unavailable";
  provenance?:
    | "missing-forecast-input"
    | "missing-local-geometry"
    | "missing-route-context"
    | "missing-official-safety-input";
};

const STORED_SAFETY_SEVERITIES = new Set([
  "green",
  "amber",
  "red",
  "unavailable",
]);
const STORED_SAFETY_CONFIDENCE = new Set(["full", "unavailable"]);
const STORED_SAFETY_PROVENANCE = new Set([
  "missing-forecast-input",
  "missing-local-geometry",
  "missing-route-context",
  "missing-official-safety-input",
]);

/** Decode stored canonical safety without accepting partial or invented rows. */
export function parseCanonicalSupProfileSafety(
  value: unknown,
): SupProfileSafetyIndicator[] | null {
  if (!Array.isArray(value)) return null;
  const parsed: SupProfileSafetyIndicator[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
      return null;
    }
    const row = item as Record<string, unknown>;
    if (
      typeof row.key !== "string" || row.key.length === 0 ||
      typeof row.label !== "string" || row.label.length === 0 ||
      typeof row.detail !== "string" || row.detail.length === 0 ||
      typeof row.severity !== "string" ||
      !STORED_SAFETY_SEVERITIES.has(row.severity) ||
      (row.confidence !== undefined &&
        (typeof row.confidence !== "string" ||
          !STORED_SAFETY_CONFIDENCE.has(row.confidence))) ||
      (row.provenance !== undefined &&
        (typeof row.provenance !== "string" ||
          !STORED_SAFETY_PROVENANCE.has(row.provenance)))
    ) {
      return null;
    }
    parsed.push({
      key: row.key,
      severity: row.severity as SupProfileSafetyIndicator["severity"],
      label: row.label,
      detail: row.detail,
      ...(row.confidence
        ? { confidence: row.confidence as NonNullable<SupProfileSafetyIndicator["confidence"]> }
        : {}),
      ...(row.provenance
        ? { provenance: row.provenance as NonNullable<SupProfileSafetyIndicator["provenance"]> }
        : {}),
    });
  }
  return parsed;
}

export function supProfileSafetyState(
  indicators: readonly SupProfileSafetyIndicator[],
): "clear" | "flagged-amber" | "flagged-red" | "no-data" {
  if (indicators.some(({ severity }) => severity === "red")) {
    return "flagged-red";
  }
  if (indicators.some(({ severity }) => severity === "amber")) {
    return "flagged-amber";
  }
  return indicators.some(({ severity }) => severity === "unavailable")
    ? "no-data"
    : "clear";
}

type SafetyProvenance = NonNullable<SupProfileSafetyIndicator["provenance"]>;

const THUNDERSTORM_CODES = new Set([95, 96, 99]);
const POLLUTION_STATES = new Set(["clear", "amber", "red", "unavailable"]);
const CLOSURE_STATES = new Set(["open", "closed", "unavailable"]);

function unavailable(
  key: string,
  label: string,
  detail: string,
  provenance: SafetyProvenance,
): SupProfileSafetyIndicator {
  return {
    key,
    severity: "unavailable",
    label,
    detail,
    confidence: "unavailable",
    provenance,
  };
}

function hazard(
  key: string,
  severity: "amber" | "red",
  label: string,
  detail: string,
): SupProfileSafetyIndicator {
  return { key, severity, label, detail, confidence: "full" };
}

function safetyNonNegative(value: number | null): number | null {
  return value !== null && Number.isFinite(value) && value >= 0 ? value : null;
}

function safetyBearing(value: number | null): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  return ((value % 360) + 360) % 360;
}

function safetyWeatherCode(value: number | null): number | null {
  return value !== null && Number.isInteger(value) && value >= 0 && value <= 99
    ? value
    : null;
}

function circularDifference(left: number, right: number): number {
  const difference = Math.abs(left - right) % 360;
  return Math.min(difference, 360 - difference);
}

function pollutionState(value: SupSurfSafetyInput["pollutionState"]) {
  return POLLUTION_STATES.has(value) ? value : "unavailable";
}

function closureState(value: SupSurfSafetyInput["closureState"]) {
  return CLOSURE_STATES.has(value) ? value : "unavailable";
}

function plannerBoolean(value: boolean | null): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function addOfficialSafety(
  rows: SupProfileSafetyIndicator[],
  prefix: "sup-surf" | "sup-downwind",
  pollution: SupSurfSafetyInput["pollutionState"],
  closure: SupSurfSafetyInput["closureState"],
): void {
  if (pollution === "unavailable") {
    rows.push(
      unavailable(
        `${prefix}-pollution`,
        "Pollution status unavailable",
        "Official water pollution advice is unavailable.",
        "missing-official-safety-input",
      ),
    );
  } else if (pollution === "red") {
    rows.push(
      hazard(
        `${prefix}-pollution`,
        "red",
        "Unsafe water quality",
        "Official pollution advice says not to enter the water.",
      ),
    );
  } else if (pollution === "amber") {
    rows.push(
      hazard(
        `${prefix}-pollution`,
        "amber",
        "Water quality warning",
        "Official pollution advice recommends extra caution.",
      ),
    );
  }

  if (closure === "unavailable") {
    rows.push(
      unavailable(
        `${prefix}-closure`,
        "Closure status unavailable",
        "Official closure information is unavailable.",
        "missing-official-safety-input",
      ),
    );
  } else if (closure === "closed") {
    rows.push(
      hazard(
        `${prefix}-closure`,
        "red",
        "Official closure",
        "The water or launch is officially closed. Do not go out.",
      ),
    );
  }
}

function addThunderstormSafety(
  rows: SupProfileSafetyIndicator[],
  prefix: "sup-surf" | "sup-downwind",
  weatherCode: number | null,
): void {
  if (weatherCode === null) {
    rows.push(
      unavailable(
        `${prefix}-thunderstorm`,
        "Thunderstorm data unavailable",
        "The weather code is missing, so lightning risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (THUNDERSTORM_CODES.has(weatherCode)) {
    rows.push(
      hazard(
        `${prefix}-thunderstorm`,
        "red",
        "Thunderstorm",
        "Lightning is extremely dangerous on open water. Do not go out.",
      ),
    );
  }
}

export function assessSupSurfSafety(
  raw: SupSurfSafetyInput,
): SupProfileSafetyIndicator[] {
  const swell = safetyNonNegative(raw.swellHeightM);
  const current = safetyNonNegative(raw.currentVelocityMs);
  const wind = safetyNonNegative(raw.windSpeedKmh);
  const rawGust = safetyNonNegative(raw.windGustKmh);
  const gust =
    rawGust !== null && (wind === null || rawGust >= wind) ? rawGust : null;
  const windDirection = safetyBearing(raw.windDirectionDeg);
  const beachFacing = safetyBearing(raw.beachFacingDeg);
  const visibility = safetyNonNegative(raw.visibilityM);
  const weatherCode = safetyWeatherCode(raw.weatherCode);
  const pollution = pollutionState(raw.pollutionState);
  const closure = closureState(raw.closureState);
  const rows: SupProfileSafetyIndicator[] = [];

  if (swell === null) {
    rows.push(
      unavailable(
        "sup-surf-sea-state",
        "Sea-state data unavailable",
        "The swell forecast is missing, so breaking-wave risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (swell > 3) {
    rows.push(
      hazard(
        "sup-surf-sea-state",
        "red",
        "Dangerous swell",
        `Swell ${swell.toFixed(1)}m is beyond the SUP Surf safety range.`,
      ),
    );
  } else if (swell > 2) {
    rows.push(
      hazard(
        "sup-surf-sea-state",
        "amber",
        "Large swell",
        `Swell ${swell.toFixed(1)}m requires advanced SUP Surf experience.`,
      ),
    );
  }

  if (current === null) {
    rows.push(
      unavailable(
        "sup-surf-current",
        "Current data unavailable",
        "Current speed is missing, so drift risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (current > 1.2) {
    rows.push(
      hazard(
        "sup-surf-current",
        "red",
        "Strong current",
        `Current ${current.toFixed(1)} m/s can overpower a paddler.`,
      ),
    );
  } else if (current > 0.8) {
    rows.push(
      hazard(
        "sup-surf-current",
        "amber",
        "Moderate current",
        `Current ${current.toFixed(1)} m/s can cause significant drift.`,
      ),
    );
  }
  rows.push(
    unavailable(
      "sup-surf-current-direction",
      "Current direction unavailable",
      "A canonical current-direction forecast is not available.",
      "missing-forecast-input",
    ),
  );

  if (wind === null) {
    rows.push(
      unavailable(
        "sup-surf-dangerous-wind",
        "Sustained wind data unavailable",
        "Sustained wind is missing, so overpower risk cannot be fully assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (wind > 40) {
    rows.push(
      hazard(
        "sup-surf-dangerous-wind",
        "red",
        "Dangerous wind",
        `Sustained wind ${Math.round(wind)} km/h is dangerous for SUP Surf.`,
      ),
    );
  } else if (wind >= 30) {
    rows.push(
      hazard(
        "sup-surf-dangerous-wind",
        "amber",
        "Strong wind",
        `Sustained wind ${Math.round(wind)} km/h requires extra caution.`,
      ),
    );
  }
  if (gust === null) {
    rows.push(
      unavailable(
        "sup-surf-gusts",
        "Gust data unavailable",
        "Gust evidence is missing or inconsistent with sustained wind.",
        "missing-forecast-input",
      ),
    );
  }

  if (beachFacing === null || windDirection === null) {
    rows.push(
      unavailable(
        "sup-surf-offshore-drift",
        "Offshore drift assessment unavailable",
        "Beach geometry and wind direction are both required to assess offshore drift.",
        beachFacing === null
          ? "missing-local-geometry"
          : "missing-forecast-input",
      ),
    );
  } else {
    const offshoreFrom = (beachFacing + 180) % 360;
    if (circularDifference(windDirection, offshoreFrom) <= 60) {
      const red =
        (wind !== null && wind >= 30) || (gust !== null && gust >= 40);
      const amber =
        (wind !== null && wind >= 15) || (gust !== null && gust >= 25);
      if (red) {
        rows.push(
          hazard(
            "sup-surf-offshore-drift",
            "red",
            "Dangerous offshore wind",
            "Offshore wind can carry a paddler away from shore.",
          ),
        );
      } else if (amber) {
        rows.push(
          hazard(
            "sup-surf-offshore-drift",
            "amber",
            "Offshore wind",
            "Offshore wind can make the return to shore difficult.",
          ),
        );
      } else if (wind === null || gust === null) {
        rows.push(
          unavailable(
            "sup-surf-offshore-drift",
            "Offshore drift assessment unavailable",
            "Complete sustained and gust evidence is required to clear offshore drift risk.",
            "missing-forecast-input",
          ),
        );
      }
    }
  }

  if (visibility === null) {
    rows.push(
      unavailable(
        "sup-surf-visibility",
        "Visibility data unavailable",
        "Visibility is missing, so shore and collision risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (visibility < 500) {
    rows.push(
      hazard(
        "sup-surf-visibility",
        "red",
        "Very poor visibility",
        `Visibility ${Math.round(visibility)}m is dangerous on the water.`,
      ),
    );
  } else if (visibility < 1_000) {
    rows.push(
      hazard(
        "sup-surf-visibility",
        "amber",
        "Reduced visibility",
        `Visibility ${Math.round(visibility)}m requires extra caution.`,
      ),
    );
  }

  addThunderstormSafety(rows, "sup-surf", weatherCode);
  addOfficialSafety(rows, "sup-surf", pollution, closure);
  return rows;
}

export function assessSupDownwindSafety(
  raw: SupDownwindSafetyInput,
): SupProfileSafetyIndicator[] {
  const sea = safetyNonNegative(raw.rideableSeaM);
  const current = safetyNonNegative(raw.currentVelocityMs);
  const wind = safetyNonNegative(raw.windSpeedKmh);
  const rawGust = safetyNonNegative(raw.windGustKmh);
  const gust =
    rawGust !== null && (wind === null || rawGust >= wind) ? rawGust : null;
  const visibility = safetyNonNegative(raw.visibilityM);
  const weatherCode = safetyWeatherCode(raw.weatherCode);
  const routeBearing = safetyBearing(raw.routeBearingDeg);
  const launchKnown = plannerBoolean(raw.launchKnown);
  const landingKnown = plannerBoolean(raw.landingKnown);
  const recoveryKnown = plannerBoolean(raw.recoveryKnown);
  const pollution = pollutionState(raw.pollutionState);
  const closure = closureState(raw.closureState);
  const rows: SupProfileSafetyIndicator[] = [];

  if (sea === null) {
    rows.push(
      unavailable(
        "sup-downwind-sea-state",
        "Sea-state data unavailable",
        "Combined sea evidence is missing, so bump risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (sea > 3.5) {
    rows.push(
      hazard(
        "sup-downwind-sea-state",
        "red",
        "Dangerous sea state",
        `Combined sea ${sea.toFixed(1)}m is dangerous for Downwind SUP.`,
      ),
    );
  } else if (sea > 2.5) {
    rows.push(
      hazard(
        "sup-downwind-sea-state",
        "amber",
        "Large sea state",
        `Combined sea ${sea.toFixed(1)}m requires advanced Downwind experience.`,
      ),
    );
  }

  if (current === null) {
    rows.push(
      unavailable(
        "sup-downwind-current",
        "Current data unavailable",
        "Current speed is missing, so route drift cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (current > 1) {
    rows.push(
      hazard(
        "sup-downwind-current",
        "red",
        "Strong current",
        `Current ${current.toFixed(1)} m/s can overwhelm route planning.`,
      ),
    );
  } else if (current > 0.5) {
    rows.push(
      hazard(
        "sup-downwind-current",
        "amber",
        "Moderate current",
        `Current ${current.toFixed(1)} m/s requires route allowance.`,
      ),
    );
  }
  rows.push(
    unavailable(
      "sup-downwind-current-direction",
      "Current direction unavailable",
      "A canonical current-direction forecast is not available.",
      "missing-forecast-input",
    ),
  );

  const redWind = (wind !== null && wind > 65) || (gust !== null && gust > 80);
  const amberWind =
    (wind !== null && wind > 50) || (gust !== null && gust > 65);
  if (redWind) {
    rows.push(
      hazard(
        "sup-downwind-dangerous-wind",
        "red",
        "Dangerous wind",
        "Wind exceeds the Downwind SUP safety range.",
      ),
    );
  } else if (amberWind) {
    rows.push(
      hazard(
        "sup-downwind-dangerous-wind",
        "amber",
        "Strong wind",
        "Wind requires expert Downwind SUP control.",
      ),
    );
  } else if (wind === null || gust === null) {
    rows.push(
      unavailable(
        "sup-downwind-dangerous-wind",
        "Wind safety data unavailable",
        "Complete sustained and gust evidence is required to clear wind risk.",
        "missing-forecast-input",
      ),
    );
  }
  if (gust === null) {
    rows.push(
      unavailable(
        "sup-downwind-gusts",
        "Gust data unavailable",
        "Gust evidence is missing or inconsistent with sustained wind.",
        "missing-forecast-input",
      ),
    );
  }

  if (visibility === null) {
    rows.push(
      unavailable(
        "sup-downwind-visibility",
        "Visibility data unavailable",
        "Visibility is missing, so navigation risk cannot be assessed.",
        "missing-forecast-input",
      ),
    );
  } else if (visibility < 500) {
    rows.push(
      hazard(
        "sup-downwind-visibility",
        "red",
        "Very poor visibility",
        `Visibility ${Math.round(visibility)}m is dangerous for route navigation.`,
      ),
    );
  } else if (visibility < 1_000) {
    rows.push(
      hazard(
        "sup-downwind-visibility",
        "amber",
        "Reduced visibility",
        `Visibility ${Math.round(visibility)}m requires extra navigation caution.`,
      ),
    );
  }

  addThunderstormSafety(rows, "sup-downwind", weatherCode);
  addOfficialSafety(rows, "sup-downwind", pollution, closure);

  if (routeBearing === null) {
    rows.push(
      unavailable(
        "sup-downwind-route",
        "Route bearing unavailable",
        "A reviewed route bearing is required for Downwind SUP.",
        "missing-route-context",
      ),
    );
  }
  for (const [key, label, known] of [
    ["sup-downwind-launch", "Launch context unavailable", launchKnown],
    ["sup-downwind-landing", "Landing context unavailable", landingKnown],
    ["sup-downwind-recovery", "Recovery context unavailable", recoveryKnown],
  ] as const) {
    if (known !== true) {
      rows.push(
        unavailable(
          key,
          label,
          "Reviewed planner context is required for this route component.",
          "missing-route-context",
        ),
      );
    }
  }
  return rows;
}

function touringSafetyAsCanonicalRows(
  input: SupTouringSafetyInput,
): SupProfileSafetyIndicator[] {
  return computeSupSafety(input).indicators.map((indicator) => ({
    ...indicator,
    key: `sup-touring-${indicator.key.replaceAll("_", "-")}`,
  }));
}

// ---------------------------------------------------------------------------
// Safety policy v2 (all-sports engine design 2.7 and 6.2; review SUP-1, SUP-2(a),
// SUP-3, SUP-4, SUP-7, SUP-V1)
// ---------------------------------------------------------------------------
//
// Used only for sports listed in CONDITIONS_SAFETY_POLICY_V2_SPORTS (the engine's
// v2 safety modules and the named transport with policy v2). The functions above
// stay the revision 1 policy, byte for byte. Rules shared by the three profiles:
// - SUP-7: no permanent placeholders. The current-direction rows (no canonical
//   direction forecast is read) and the planner-context rows (route, launch,
//   landing and recovery, which no source supplies) are gone: route curation is
//   an applicability question (the revision 2 kernel and WP-E), not a forecast
//   hazard. Official pollution and closure rows appear only when the caller
//   supplies an official state (a wired feed); without one (undefined or null)
//   the dimension has no row, because no official feed exists yet (DIVSWIM-5).
//   A supplied "unavailable" still gives an unavailable row.
// - The standalone "Gust data unavailable" rows are gone: a missing gust is
//   judged inside the rows that read it (wind and offshore drift), which report
//   unavailable when the missing gust could hide a hazard, never clear.
// - A missing weather code gives "Storm forecast unavailable" (design 2.5).
// - Offshore drift clears when the wind is known to be below the amber threshold,
//   whatever the geometry (no wind, no drift); otherwise missing geometry or wind
//   direction is unavailable.
// - Owner decision O-07: the current is an optional safety input, so a missing
//   current is the neutral "no current" and adds no row (a known one is judged
//   at the profile's thresholds). Many coastal spots have no current cell, and an
//   unavailable row there would keep every day from reading clear.
// - SUP Surf's current is judged by direction at beach breaks (accelerated
//   rollout Phase C preparation, seaward-current.ts), as Touring's row is: amber
//   above the profile's amber speed in any direction; red when the seaward
//   component is above its red speed or, at a beach break, the speed is above
//   2.0 m/s whichever way it sets; at reef, point and river-mouth breaks (and a
//   spot with no recorded type) the speed alone above the red speed, as revision
//   1 read it. A caller that supplies no seaward component counts the whole
//   speed as seaward, and no relief unless it asks for one. Where the relief, not
//   the speed, keeps the row amber (a stream along the beach faster than the red
//   speed), the row says so: "Strong current along the coast", its own event key,
//   never "Moderate current" (after review, 1 October 2026: such a stream is
//   faster than a paddler can paddle back against).
// - Downwind's current takes no along-coast relief (withdrawn after review on
//   1 October 2026): amber above 0.5 m/s and red above 1.0 m/s whichever way it
//   sets, as revision 1 read it. A run is ridden in the stream along a curated
//   route, often against it with the run's own wind against the stream, so a
//   stream along the beach is no safer for it. The seaward component only words
//   the red.
// - Downwind's wind against tide (accelerated rollout Phase D, 1 October 2026;
//   wind-against-tide.ts): on its curated route, a stream of 0.75 m/s or more
//   within 45 degrees of straight into a wind of 15 kn (28 km/h) or more is red,
//   and one of 0.5 m/s or more against 12 kn (22 km/h) or more amber: an opposing
//   stream steepens the wind sea the run rides until it breaks. Its own event
//   key, after the wind row.
// - Downwind's red gust is revision 1's: above 80 km/h on the modelled gust as it
//   stands (SUP_DOWNWIND_V2_WIND). The accelerated rollout Phase D read it as 92 km/h
//   against the land-cell gust, from Irish weather buoys, and withdrew it after review
//   the same day (gates addendum A14): the buoy gusts were never raised from their
//   4.5 m anemometers to 10 m, and with that done the even chance of an 80 km/h gust
//   falls to a modelled 83 to 84 km/h (45.0 to 45.2 kn, bootstrap 95% from about 81
//   to 87 km/h), resting on 7 paired hours above 48 kn; 80 km/h is the cautious end
//   of that range and revision 1's value.
// - Owner decision O-17 (review SUP-3, "apply one policy on every path"): SUP Surf
//   and Downwind take Touring's UV and storm rows (sup-scoring.ts
//   supUvAndStormSafetyV2: UV amber above 8 and red at 11 or more, CAPE-only
//   amber, CAPE above 500 J/kg with convective rain amber), under their own key
//   prefix and in the UV then thunderstorm dimensions after visibility. The
//   thunderstorm and storm-forecast keys they already used are kept.

export type SupOfficialState = Readonly<{
  pollutionState?: SupSurfSafetyInput["pollutionState"] | null;
  closureState?: SupSurfSafetyInput["closureState"] | null;
}>;

export type SupSurfSafetyInputV2 = SupOfficialState & {
  /**
   * SUP-4: the largest present partition height after the shared hourly shelter, when it is
   * known exactly (supSurfSafetySea: the combined height or the swell height is present), else null.
   */
  seaStateM: number | null;
  /**
   * A lower bound of the sheltered sea when only the wind sea is known (the swell, which could be
   * far larger, is missing), else null. It flags when it already exceeds a threshold; otherwise
   * the row is unavailable, never clear.
   */
  seaStateMinM: number | null;
  currentVelocityMs: number | null;
  /** The current's seaward component in m/s; null or omitted when unresolved (the whole speed counts). */
  currentSeawardMs?: number | null;
  /** True only at a beach break (seaward-current.ts); omitted: no along-coast relief. */
  currentAlongCoastRelief?: boolean;
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  windDirectionDeg: number | null;
  beachFacingDeg: number | null;
  visibilityM: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
  /** The UV index where the hour's presence shows it, else null (read as "UV data unavailable"). */
  uvIndex: number | null;
  weatherCode: number | null;
  /** CAPE in J/kg, null when the provider had none. */
  capeJkg: number | null;
};

export type SupDownwindSafetyInputV2 = SupOfficialState & {
  /** SUP-V1: the undirected combined sea (no swell direction weighting) when known exactly. */
  combinedSeaM: number | null;
  /** A lower bound of the combined sea from the one partition that is known, else null. */
  combinedSeaMinM: number | null;
  currentVelocityMs: number | null;
  /**
   * The current's seaward component in m/s, which only words the red row; null or omitted when
   * unresolved. Downwind takes no along-coast relief (see the section header).
   */
  currentSeawardMs?: number | null;
  /** Where the hour's current flows to, in degrees (wind against tide); null or omitted when missing. */
  currentTowardsDeg?: number | null;
  /**
   * The same hour's stream speed (m/s) for the wind-against-tide row, where `currentVelocityMs` is
   * not that hour's (the interim day summary passes its daylight peak there). Omitted: the row
   * reads `currentVelocityMs`; null: no stream that hour.
   */
  windAgainstTideStreamMs?: number | null;
  /**
   * The curated downwind route's bearing in degrees (wind against tide). Null or omitted off a
   * curated route: no wind-against-tide row (wind-against-tide.ts).
   */
  routeBearingDeg?: number | null;
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  windDirectionDeg: number | null;
  beachFacingDeg: number | null;
  visibilityM: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
  /** The UV index where the hour's presence shows it, else null (read as "UV data unavailable"). */
  uvIndex: number | null;
  weatherCode: number | null;
  /** CAPE in J/kg, null when the provider had none. */
  capeJkg: number | null;
};

/**
 * Downwind's wind thresholds under policy v2, in km/h, revision 1's: red on a sustained wind above
 * 65 or a gust above 80, amber on a sustained wind above 50 or a gust above 65 (Phase D's 92 km/h
 * reading was withdrawn after review; see the section header).
 */
export const SUP_DOWNWIND_V2_WIND = Object.freeze({ amberSustainedKmh: 50, redSustainedKmh: 65, amberGustKmh: 65, redGustKmh: 80 });

/**
 * The undirected combined sea of an hour for Downwind safety under policy v2 (SUP-V1): both
 * partitions when known, else the combined wave height, else a lower bound from the one known
 * partition. Swell direction never weights it, so an opposed or missing swell never vanishes.
 */
export function supUndirectedCombinedSea(
  swellM: number | null,
  windWaveM: number | null,
  combinedM: number | null,
): Readonly<{ exact: number | null; min: number | null }> {
  if (swellM !== null && windWaveM !== null) {
    const magnitude = Math.hypot(swellM, windWaveM);
    return { exact: Number.isFinite(magnitude) ? magnitude : Number.MAX_VALUE, min: null };
  }
  if (combinedM !== null) return { exact: combinedM, min: null };
  return { exact: null, min: swellM ?? windWaveM };
}

/**
 * The unsheltered sea of an hour for SUP Surf safety under policy v2: the largest present
 * partition. It is exact when the combined height (which bounds both partitions) or the swell
 * height (the breaking-wave partition the row judges) is present. With only the wind sea known it
 * is a lower bound, because a missing swell could be far larger and must never read as clear.
 * Callers apply the shared hourly shelter to both values (SUP-4).
 */
export function supSurfSafetySea(
  swellM: number | null,
  windWaveM: number | null,
  combinedM: number | null,
): Readonly<{ exact: number | null; min: number | null }> {
  if (combinedM !== null || swellM !== null) {
    const heights = [swellM, combinedM, windWaveM].filter((height): height is number => height !== null);
    return { exact: Math.max(...heights), min: null };
  }
  return { exact: null, min: windWaveM };
}

// SUP Surf's offshore-drift thresholds (its approved revision 1 values: 15 and 25 km/h amber, 30
// km/h sustained or 40 km/h gusts red; the Phase D lane's 50 km/h gust was withdrawn after review)
// live beside SUP-1 in sup-offshore-drift.ts, which the coastal page briefing also imports
// (P2C-WP9-04), and are re-exported here.
export { SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS } from "./sup-offshore-drift";

/** SUP-2(a): Downwind offshore displacement uses the SUP-1 thresholds and geometry. */
export const SUP_DOWNWIND_OFFSHORE_DISPLACEMENT_THRESHOLDS: SupOffshoreDriftThresholds = SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS;

function addOfficialSafetyV2(
  rows: SupProfileSafetyIndicator[],
  prefix: "sup-surf" | "sup-downwind",
  official: SupOfficialState,
): void {
  const pollution = official.pollutionState;
  const closure = official.closureState;
  if (pollution !== undefined && pollution !== null) {
    addOfficialSafety(rows, prefix, pollutionState(pollution), "open");
  }
  if (closure !== undefined && closure !== null) {
    addOfficialSafety(rows, prefix, "clear", closureState(closure));
  }
}

/**
 * Touring's SUP-3 row keys (sup-scoring.ts supUvAndStormSafetyV2) as SUP Surf and Downwind key
 * suffixes. The thunderstorm and storm-forecast suffixes are the ones these profiles already used.
 */
export const SUP_PROFILE_UV_STORM_KEY_SUFFIXES: Readonly<Record<string, string>> = Object.freeze({
  uv_unavailable: "uv-unavailable",
  uv_extreme: "uv-extreme",
  uv_very_high: "uv-very-high",
  lightning: "thunderstorm",
  lightning_risk: "thunderstorm-risk",
  lightning_possible: "thunderstorms-possible",
  storm_forecast: "storm-forecast",
});

/** The shared SUP-3 UV and storm rows (O-17) under a profile's key prefix. */
function addUvAndStormSafetyV2(
  rows: SupProfileSafetyIndicator[],
  prefix: "sup-surf" | "sup-downwind",
  input: SupUvAndStormInput,
): void {
  for (const indicator of supUvAndStormSafetyV2(input)) {
    const key = `${prefix}-${SUP_PROFILE_UV_STORM_KEY_SUFFIXES[indicator.key] ?? indicator.key.replaceAll("_", "-")}`;
    if (indicator.severity === "unavailable") {
      rows.push(unavailable(key, indicator.label, indicator.detail, indicator.provenance ?? "missing-forecast-input"));
    } else if (indicator.severity === "amber" || indicator.severity === "red") {
      rows.push(hazard(key, indicator.severity, indicator.label, indicator.detail));
    }
  }
}

function addDriftSafetyV2(
  rows: SupProfileSafetyIndicator[],
  key: string,
  assessment: SupOffshoreDriftAssessment,
  text: Readonly<{ redDetail: string; amberDetail: string; unavailableLabel: string }>,
): void {
  if (assessment.level === "red") {
    rows.push(hazard(key, "red", "Dangerous offshore wind", text.redDetail));
  } else if (assessment.level === "amber") {
    rows.push(hazard(key, "amber", "Offshore wind", text.amberDetail));
  } else if (assessment.level === "unavailable") {
    rows.push(unavailable(
      key,
      text.unavailableLabel,
      assessment.provenance === "missing-local-geometry"
        ? "Beach geometry is required to assess offshore wind."
        : "Wind direction and complete wind evidence are required to assess offshore wind.",
      assessment.provenance,
    ));
  }
}

/**
 * The current row, judged by direction where the caller asks for the along-coast relief (see the
 * section header); a missing current is the neutral "no current" and adds no row (O-07). An amber
 * current above the red speed (one only the relief keeps amber) takes its own event key,
 * `${key}-along-coast`, and says what it is.
 */
function addCurrentSafety(
  rows: SupProfileSafetyIndicator[],
  key: string,
  current: number | null,
  direction: Readonly<{ seawardMs?: number | null; alongCoastRelief?: boolean }>,
  thresholds: Readonly<{ amber: number; red: number }>,
  text: Readonly<{ red: string; redSeaward: string; amber: string }>,
): void {
  if (current === null) return;
  const judged = classifySuppliedCurrent(current, direction.seawardMs, {
    amberAboveMs: thresholds.amber,
    redSeawardAboveMs: thresholds.red,
    alongCoastRelief: direction.alongCoastRelief === true,
  });
  if (judged.level === "red") {
    const detail = judged.reason === "seaward" && judged.resolved
      ? text.redSeaward.replace("{seaward}", judged.seawardMs.toFixed(1))
      : text.red;
    rows.push(hazard(key, "red", "Strong current", detail.replace("{value}", current.toFixed(1))));
  } else if (judged.level === "amber" && current > thresholds.red) {
    rows.push(hazard(`${key}-along-coast`, "amber", SUP_ALONG_COAST_CURRENT_LABEL, supAlongCoastCurrentDetail(current)));
  } else if (judged.level === "amber") {
    rows.push(hazard(key, "amber", "Moderate current", text.amber.replace("{value}", current.toFixed(1))));
  }
}

/** The visibility row: model fog (model-fog.ts, accelerated rollout Phase E), red only in saturated air. */
function addVisibilitySafety(
  rows: SupProfileSafetyIndicator[],
  key: string,
  visibility: number | null,
  relativeHumidityPct: number | null | undefined,
  text: Readonly<{ unavailable: string; red: string; amber: string }>,
): void {
  const fog = classifyModelFog({ visibilityM: visibility, relativeHumidityPct });
  if (fog === "unavailable") {
    rows.push(unavailable(key, "Visibility data unavailable", text.unavailable, "missing-forecast-input"));
  } else if (fog === "red") {
    rows.push(hazard(key, "red", "Very poor visibility", text.red.replace("{value}", String(Math.round(visibility as number)))));
  } else if (fog === "amber") {
    rows.push(hazard(key, "amber", "Reduced visibility", text.amber.replace("{value}", String(Math.round(visibility as number)))));
  }
}

/** SUP Surf per-hour safety under policy v2 (see the section header). */
export function assessSupSurfSafetyV2(raw: SupSurfSafetyInputV2): SupProfileSafetyIndicator[] {
  const sea = safetyNonNegative(raw.seaStateM);
  const seaMin = safetyNonNegative(raw.seaStateMinM);
  const current = safetyNonNegative(raw.currentVelocityMs);
  const wind = safetyNonNegative(raw.windSpeedKmh);
  const rawGust = safetyNonNegative(raw.windGustKmh);
  const gust = rawGust !== null && (wind === null || rawGust >= wind) ? rawGust : null;
  const visibility = safetyNonNegative(raw.visibilityM);
  const weatherCode = safetyWeatherCode(raw.weatherCode);
  const rows: SupProfileSafetyIndicator[] = [];

  // A lower bound flags only when it already exceeds a threshold; below that it is unavailable.
  const seaLow = sea ?? seaMin;
  if (seaLow !== null && seaLow > 3) {
    rows.push(hazard("sup-surf-sea-state", "red", "Dangerous swell", `Swell ${seaLow.toFixed(1)}m is beyond the SUP Surf safety range.`));
  } else if (seaLow !== null && seaLow > 2) {
    rows.push(hazard("sup-surf-sea-state", "amber", "Large swell", `Swell ${seaLow.toFixed(1)}m requires advanced SUP Surf experience.`));
  } else if (sea === null) {
    rows.push(unavailable("sup-surf-sea-state", "Sea-state data unavailable", "The swell forecast is missing, so breaking-wave risk cannot be assessed.", "missing-forecast-input"));
  }

  addCurrentSafety(rows, "sup-surf-current", current, { seawardMs: raw.currentSeawardMs, alongCoastRelief: raw.currentAlongCoastRelief }, { amber: 0.8, red: 1.2 }, {
    red: "Current {value} m/s can overpower a paddler.",
    redSeaward: "Current {value} m/s, {seaward} m/s of it setting offshore, can carry a paddler out to sea.",
    amber: "Current {value} m/s can cause significant drift.",
  });

  if (wind !== null && wind > 40) {
    rows.push(hazard("sup-surf-dangerous-wind", "red", "Dangerous wind", `Sustained wind ${Math.round(wind)} km/h is dangerous for SUP Surf.`));
  } else if (wind !== null && wind >= 30) {
    rows.push(hazard("sup-surf-dangerous-wind", "amber", "Strong wind", `Sustained wind ${Math.round(wind)} km/h requires extra caution.`));
  } else if (wind === null && (gust === null || gust >= 30)) {
    // Sustained wind never exceeds its gust, so a gust below 30 km/h rules the flag out.
    rows.push(unavailable("sup-surf-dangerous-wind", "Sustained wind data unavailable", "Sustained wind is missing, so overpower risk cannot be fully assessed.", "missing-forecast-input"));
  }

  addDriftSafetyV2(rows, "sup-surf-offshore-drift", assessSupOffshoreDrift({
    windSpeedKmh: wind,
    windGustKmh: rawGust,
    windDirectionDeg: raw.windDirectionDeg,
    beachFacingDeg: raw.beachFacingDeg,
  }, SUP_SURF_OFFSHORE_DRIFT_THRESHOLDS), {
    redDetail: "Offshore wind can carry a paddler away from shore.",
    amberDetail: "Offshore wind can make the return to shore difficult.",
    unavailableLabel: "Offshore drift assessment unavailable",
  });

  addVisibilitySafety(rows, "sup-surf-visibility", visibility, raw.relativeHumidityPct, {
    unavailable: "Visibility is missing, so shore and collision risk cannot be assessed.",
    red: "Visibility {value}m is dangerous on the water.",
    amber: "Visibility {value}m requires extra caution.",
  });
  addUvAndStormSafetyV2(rows, "sup-surf", { uvIndex: raw.uvIndex, weatherCode, capeJkg: raw.capeJkg });
  addOfficialSafetyV2(rows, "sup-surf", raw);
  return rows;
}

/** Downwind SUP per-hour safety under policy v2 (see the section header). */
export function assessSupDownwindSafetyV2(raw: SupDownwindSafetyInputV2): SupProfileSafetyIndicator[] {
  const sea = safetyNonNegative(raw.combinedSeaM);
  const seaMin = safetyNonNegative(raw.combinedSeaMinM);
  const current = safetyNonNegative(raw.currentVelocityMs);
  const wind = safetyNonNegative(raw.windSpeedKmh);
  const rawGust = safetyNonNegative(raw.windGustKmh);
  const gust = rawGust !== null && (wind === null || rawGust >= wind) ? rawGust : null;
  const visibility = safetyNonNegative(raw.visibilityM);
  const weatherCode = safetyWeatherCode(raw.weatherCode);
  const rows: SupProfileSafetyIndicator[] = [];

  // SUP-V1: an opposed or missing swell never vanishes from the sea state, and a known partition
  // already beyond a threshold flags even when the other is missing.
  const seaLow = sea ?? seaMin;
  if (seaLow !== null && seaLow > 3.5) {
    rows.push(hazard("sup-downwind-sea-state", "red", "Dangerous sea state", `Combined sea ${seaLow.toFixed(1)}m is dangerous for Downwind SUP.`));
  } else if (seaLow !== null && seaLow > 2.5) {
    rows.push(hazard("sup-downwind-sea-state", "amber", "Large sea state", `Combined sea ${seaLow.toFixed(1)}m requires advanced Downwind experience.`));
  } else if (sea === null) {
    rows.push(unavailable("sup-downwind-sea-state", "Sea-state data unavailable", "Combined sea evidence is missing, so bump risk cannot be assessed.", "missing-forecast-input"));
  }

  // No along-coast relief for a downwind run (see the section header).
  addCurrentSafety(rows, "sup-downwind-current", current, { seawardMs: raw.currentSeawardMs, alongCoastRelief: false }, { amber: 0.5, red: 1 }, {
    red: "Current {value} m/s can overwhelm route planning.",
    redSeaward: "Current {value} m/s, {seaward} m/s of it setting offshore, can carry a downwind paddler out to sea.",
    amber: "Current {value} m/s requires route allowance.",
  });

  // A missing gust is bounded below by the sustained wind and a missing sustained wind above by the gust.
  const gustLow = gust ?? wind;
  const windHigh = wind ?? gust;
  const thresholds = SUP_DOWNWIND_V2_WIND;
  const redWind = (wind !== null && wind > thresholds.redSustainedKmh) || (gustLow !== null && gustLow > thresholds.redGustKmh);
  const amberWind = (wind !== null && wind > thresholds.amberSustainedKmh) || (gustLow !== null && gustLow > thresholds.amberGustKmh);
  if (redWind) {
    rows.push(hazard("sup-downwind-dangerous-wind", "red", "Dangerous wind", "Wind exceeds the Downwind SUP safety range."));
  } else if (amberWind) {
    rows.push(hazard("sup-downwind-dangerous-wind", "amber", "Strong wind", "Wind requires expert Downwind SUP control."));
  } else if (windHigh === null || windHigh > thresholds.amberSustainedKmh || gust === null) {
    rows.push(unavailable("sup-downwind-dangerous-wind", "Wind safety data unavailable", "Complete sustained and gust evidence is required to clear wind risk.", "missing-forecast-input"));
  }

  // Wind against tide on the curated route (wind-against-tide.ts; see the section header).
  const againstTideStream = raw.windAgainstTideStreamMs !== undefined ? safetyNonNegative(raw.windAgainstTideStreamMs) : current;
  const againstTide = classifyWindAgainstTide({
    routeBearingDeg: raw.routeBearingDeg ?? null,
    windKmh: wind,
    windFromDeg: raw.windDirectionDeg,
    gustKmh: gust,
    streamMs: againstTideStream,
    streamTowardsDeg: raw.currentTowardsDeg ?? null,
  });
  if (againstTide.level === "red" || againstTide.level === "amber") {
    rows.push(hazard("sup-downwind-wind-against-tide", againstTide.level, "Wind against tide", windAgainstTideDetail(againstTide, wind, againstTideStream as number)));
  } else if (againstTide.level === "unavailable") {
    rows.push(unavailable("sup-downwind-wind-against-tide", "Wind against tide unavailable", "The wind forecast is missing, so a stream setting against the wind cannot be assessed.", "missing-forecast-input"));
  }

  addDriftSafetyV2(rows, "sup-downwind-offshore-displacement", assessSupOffshoreDrift({
    windSpeedKmh: wind,
    windGustKmh: rawGust,
    windDirectionDeg: raw.windDirectionDeg,
    beachFacingDeg: raw.beachFacingDeg,
  }, SUP_DOWNWIND_OFFSHORE_DISPLACEMENT_THRESHOLDS), {
    redDetail: "Offshore wind can carry a downwind paddler out to sea.",
    amberDetail: "Offshore wind can push the run away from the coast.",
    unavailableLabel: "Offshore displacement assessment unavailable",
  });

  addVisibilitySafety(rows, "sup-downwind-visibility", visibility, raw.relativeHumidityPct, {
    unavailable: "Visibility is missing, so navigation risk cannot be assessed.",
    red: "Visibility {value}m is dangerous for route navigation.",
    amber: "Visibility {value}m requires extra navigation caution.",
  });
  addUvAndStormSafetyV2(rows, "sup-downwind", { uvIndex: raw.uvIndex, weatherCode, capeJkg: raw.capeJkg });
  addOfficialSafetyV2(rows, "sup-downwind", raw);
  return rows;
}

/** Touring per-hour safety under policy v2, as canonical profile rows (keys prefixed "sup-touring-"). */
export function assessSupTouringSafetyV2(input: SupSafetyV2Input): SupProfileSafetyIndicator[] {
  return computeSupSafetyV2(input).indicators.map((indicator) => ({
    ...indicator,
    key: `sup-touring-${indicator.key.replaceAll("_", "-")}`,
  }));
}

export type SupProfileSafetyInputV2 = SupSafetyV2Input | SupSurfSafetyInputV2 | SupDownwindSafetyInputV2;

export function assessSupProfileSafetyV2(profile: "sup-touring", input: SupSafetyV2Input): SupProfileSafetyIndicator[];
export function assessSupProfileSafetyV2(profile: "sup-surf", input: SupSurfSafetyInputV2): SupProfileSafetyIndicator[];
export function assessSupProfileSafetyV2(profile: "sup-downwind", input: SupDownwindSafetyInputV2): SupProfileSafetyIndicator[];
export function assessSupProfileSafetyV2(profile: SupProfileKey, input: SupProfileSafetyInputV2): SupProfileSafetyIndicator[] {
  switch (profile) {
    case "sup-surf":
      return assessSupSurfSafetyV2(input as SupSurfSafetyInputV2);
    case "sup-downwind":
      return assessSupDownwindSafetyV2(input as SupDownwindSafetyInputV2);
    case "sup-touring":
      return assessSupTouringSafetyV2(input as SupSafetyV2Input);
  }
}

export function assessSupProfileSafety(
  profile: "sup-touring",
  input: SupTouringSafetyInput,
): SupProfileSafetyIndicator[];
export function assessSupProfileSafety(
  profile: "sup-surf",
  input: SupSurfSafetyInput,
): SupProfileSafetyIndicator[];
export function assessSupProfileSafety(
  profile: "sup-downwind",
  input: SupDownwindSafetyInput,
): SupProfileSafetyIndicator[];
export function assessSupProfileSafety(
  profile: SupProfileKey,
  input: SupProfileSafetyInput,
): SupProfileSafetyIndicator[] {
  switch (profile) {
    case "sup-surf":
      return assessSupSurfSafety(input as SupSurfSafetyInput);
    case "sup-downwind":
      return assessSupDownwindSafety(input as SupDownwindSafetyInput);
    case "sup-touring":
      return touringSafetyAsCanonicalRows(input as SupTouringSafetyInput);
  }
}
